import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { archiveCatalogArtifact, loadArtifactCatalog, type ArtifactCatalog, type ArtifactLocation, type CatalogArtifact } from "./catalog";
import { DocumentView } from "./DocumentView";

export function ArtifactBrowser({ location }: { location: ArtifactLocation }) {
  const key = JSON.stringify([location.accountId, location.workspaceId, location.projectId]);
  const [state, setState] = useState<{ key: string; catalog?: ArtifactCatalog; failed?: boolean }>();
  const [opened, setOpened] = useState<CatalogArtifact>();
  const [confirming, setConfirming] = useState<CatalogArtifact>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [revision, setRevision] = useState(0);
  const lifetime = useRef<AbortController | undefined>(undefined);
  useEffect(() => {
    const controller = new AbortController(); lifetime.current = controller;
    setState(undefined); setOpened(undefined); setConfirming(undefined); setBusy(false); setMessage("");
    void loadArtifactCatalog(location, controller.signal).then(
      (catalog) => { if (!controller.signal.aborted) setState({ key, catalog }); },
      () => { if (!controller.signal.aborted) setState({ key, failed: true }); },
    );
    function refresh() { controller.abort(); setState(undefined); setOpened(undefined); setConfirming(undefined); setRevision((value) => value + 1); }
    window.addEventListener("focus", refresh);
    return () => { controller.abort(); window.removeEventListener("focus", refresh); };
  }, [key, revision]);

  const current = state?.key === key ? state : undefined;
  const catalog = current?.catalog;
  async function archive() {
    const signal = lifetime.current?.signal;
    if (!catalog || !confirming || busy || !signal || signal.aborted) return;
    setBusy(true); setMessage("");
    try {
      const updated = await archiveCatalogArtifact(location, confirming, catalog.scope, signal);
      if (!signal.aborted) { setState({ key, catalog: updated }); setConfirming(undefined); setMessage("Document archived. Saved conversation versions are kept."); }
    } catch {
      if (!signal.aborted) {
        // A lost response may have archived the record. Never claim success or
        // retain private names without a fresh authorized catalog.
        setState({ key, failed: true }); setConfirming(undefined); setOpened(undefined);
        setMessage("Archive couldn't be confirmed. Reload documents to check the result.");
      }
    } finally { if (!signal.aborted) setBusy(false); }
  }

  if (opened && catalog) return <DocumentView selection={{ catalog: opened, scope: catalog.scope }} onClose={() => setOpened(undefined)} />;
  return <section className="mt-10" aria-label="Documents">
    <h2 className="mb-3 text-lg font-semibold">Documents</h2>
    {!current ? <p className="py-4 text-sm text-[#52606e]" role="status">Loading documents…</p> : !catalog ? <>
      <p className="py-4 text-sm text-[#52606e]" role="alert">{message || "Documents couldn't be loaded. Your access may have changed."}</p>
      <Button variant="ghost" onClick={() => setRevision((value) => value + 1)}>Reload documents</Button>
    </> : <>
      {message ? <p className="mb-3 text-sm text-[#52606e]" role="status">{message}</p> : null}
      {catalog.artifacts.length ? <ul className="divide-y divide-[#cfe4f1] border-y border-[#cfe4f1]">
        {catalog.artifacts.map((artifact) => <li key={artifact.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
          <button className="min-w-0 flex-1 rounded-md px-2 py-2 text-left break-words hover:bg-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8]" disabled={busy} onClick={() => { setConfirming(undefined); setOpened(artifact); }}>{artifact.title || "Untitled document"}</button>
          {catalog.canArchive ? <Button variant="ghost" disabled={busy} aria-label={`Archive ${artifact.title || "untitled document"}`} onClick={() => { setMessage(""); setConfirming(artifact); }}>Archive</Button> : null}
        </li>)}
      </ul> : <p className="py-4 text-sm text-[#52606e]">No documents here yet. Upload a file in a conversation to add one.</p>}
      {confirming ? <div className="mt-4 rounded-lg border border-[#cfe4f1] bg-white/70 p-4" role="group" aria-label="Confirm archive">
        <p className="break-words text-sm">Archive “{confirming.title || "Untitled document"}”? It will leave this list. Saved versions in conversations will remain available to people with access.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button disabled={busy} onClick={() => void archive()}>{busy ? "Archiving…" : "Confirm archive"}</Button>
          <Button variant="ghost" disabled={busy} onClick={() => setConfirming(undefined)}>Cancel</Button>
        </div>
      </div> : null}
    </>}
  </section>;
}
